// Dashboard con tabs y rango propio por tab.
// Cada tab refetchea sólo sus datos al cambiar su rango (no se actualiza todo a la vez).
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { translate } from 'react-translate';
import { useDispatch, useSelector } from 'react-redux';
import { Doughnut, Bar, Line } from 'react-chartjs-2';
import { Input } from 'reactstrap';
import moment from 'moment';

import {
  actionGetAllDoctorsSystem,
  actionGetAllPatientsSystem,
  actionGetAllHl7Consent,
} from 'store/actions/systemActions';
import { actionGetAllPractices, actionGetPractices } from 'store/actions/practicesActions';
import { actionGetBilling } from 'store/actions/billingActions';
import {
  actionGetKitsCount,
  actionGetKitsStateCount,
  actionGetKitTypes,
} from 'store/actions/kitsActions';
import LabStatsService from 'services/LabStatsService';
import PracticesService from 'services/PracticesService';

// ---------- palette ----------
const C = {
  primary: '#2c8af8',
  info:    '#1c8ee0',
  success: '#18ce0f',
  warning: '#ff8d72',
  danger:  '#fb404b',
  muted:   '#8a92a6',
  dark:    '#2c2c2c',
};
const KIT_STATUS_COLOR = {
  k_st_1:           C.muted,
  k_st_disassembled:         C.muted,
  k_st_2:                C.success,
  k_st_reserved:             C.info,
  k_st_3: C.info,
  k_st_4:  C.primary,
  'k_st_5':      C.warning,
  k_st_9:       '#f5a623',
  k_st_6:             C.danger,
  k_st_8:               C.danger,
  k_st_10:              C.warning,
  k_st_11:                  C.dark,
  k_st_7:              C.muted,
};
const RANGE_PRESETS = [
  { key: 7,   label: '7d' },
  { key: 30,  label: '30d' },
  { key: 90,  label: '90d' },
  { key: 365, label: '1y' },
];

// ---------- helpers ----------
const fmtNum = (v) => (typeof v === 'number' ? v.toLocaleString() : v == null ? '—' : v);
const calcDelta = (current, prev) => {
  if (!prev || prev === 0) return null;
  const pct = ((current - prev) / prev) * 100;
  return Math.round(pct * 10) / 10;
};
const formatBytes = (bytes) => {
  if (!bytes || bytes < 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};
const buildLineFromHistory = (series) => {
  const labels = series.map((p) => moment(p.timestamp).format('DD MMM HH:mm'));
  return {
    labels,
    datasets: [
      {
        label: 'Requests',
        data: series.map((p) => p.requests || p.totalRequests || 0),
        borderColor: '#1c8ee0',
        backgroundColor: 'rgba(28,142,224,0.12)',
        borderWidth: 2,
        pointRadius: 2,
        fill: true,
        tension: 0.3,
      },
      {
        label: 'Files uploaded',
        data: series.map((p) => p.filesUploaded || 0),
        borderColor: '#2c8af8',
        backgroundColor: 'rgba(44,138,248,0.12)',
        borderWidth: 2,
        pointRadius: 2,
        fill: true,
        tension: 0.3,
      },
    ],
  };
};

// ---------- subcomponents ----------

const RangeSelector = ({ days, onChange, lastUpdated, onRefresh, t, hideLabel }) => (
  <div className="dash-section-toolbar">
    {!hideLabel && (
      <span className="dash-toolbar-meta">
        {t('range') || 'Range'}
      </span>
    )}
    <div className="dash-range-presets" role="tablist">
      {RANGE_PRESETS.map((r) => (
        <button
          key={r.key}
          type="button"
          className={days === r.key ? 'active' : ''}
          onClick={() => onChange(r.key)}
        >
          {r.label}
        </button>
      ))}
    </div>
    <button
      type="button"
      className="dash-refresh-btn"
      onClick={onRefresh}
      title={t('refresh') || 'Refresh'}
      aria-label="Refresh"
    >
      <i className="now-ui-icons arrows-1_refresh-69" />
    </button>
    {lastUpdated && (
      <span className="dash-toolbar-meta">
        {t('updated') || 'Updated'} · {moment(lastUpdated).fromNow()}
      </span>
    )}
  </div>
);

const Section = ({ icon, title, hint, toolbar, children }) => (
  <div className="dash-section">
    <div className="dash-section-header">
      {icon && <i className={icon} />}
      <h5>{title}</h5>
      {hint && <span className="dash-section-hint">{hint}</span>}
      {toolbar && <div className="dash-section-toolbar-wrap">{toolbar}</div>}
    </div>
    <div className="dash-section-body">{children}</div>
  </div>
);

const MetricCard = ({ label, value, delta, deltaLabel, icon, tone = 'info' }) => {
  const deltaCls = delta == null ? 'flat' : delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
  const arrow    = delta == null ? '·' : delta > 0 ? '↑' : delta < 0 ? '↓' : '·';
  return (
    <div className={`dash-metric-card tone-${tone}`}>
      <div className="dash-metric-head">
        <span className="dash-metric-label">{label}</span>
        {icon && <span className="dash-metric-icon"><i className={icon} /></span>}
      </div>
      <div className="dash-metric-value">{fmtNum(value)}</div>
      <div className="dash-metric-foot">
        <span className={`dash-metric-delta ${deltaCls}`}>
          {arrow} {delta == null ? '—' : `${Math.abs(delta)}%`}
        </span>
        <span>{deltaLabel}</span>
      </div>
    </div>
  );
};

const DonutWithLegend = ({ items, total, t }) => {
  const filtered = (items || []).filter((it) => it.value > 0);
  if (!filtered.length) return <div className="dash-empty">{t('no-data') || 'No data available'}</div>;

  const data = {
    labels: filtered.map((i) => i.label),
    datasets: [{
      data: filtered.map((i) => i.value),
      backgroundColor: filtered.map((i) => i.color),
      borderColor: '#fff',
      borderWidth: 2,
    }],
  };
  const options = {
    cutoutPercentage: 65,
    legend: { display: false },
    maintainAspectRatio: false,
    tooltips: {
      callbacks: {
        label: (item, d) => {
          const value = d.datasets[0].data[item.index];
          const lbl   = d.labels[item.index];
          const pct   = total ? Math.round((value / total) * 1000) / 10 : 0;
          return `${lbl}: ${value.toLocaleString()} (${pct}%)`;
        },
      },
    },
  };

  return (
    <div className="dash-donut-row">
      <div className="dash-chart-wrap dash-chart-wrap-sm">
        <Doughnut data={data} options={options} />
      </div>
      <ul className="dash-legend">
        {filtered.map((i) => {
          const pct = total ? Math.round((i.value / total) * 1000) / 10 : 0;
          return (
            <li key={i.key} title={i.tooltip}>
              <span className="legend-dot" style={{ background: i.color }} />
              <span className="legend-label">{i.label}</span>
              <span className="legend-value">{fmtNum(i.value)}<small>{pct}%</small></span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const Funnel = ({ rows, total }) => {
  if (!rows || !rows.length) return <div className="dash-empty">No data</div>;
  return (
    <div className="dash-funnel">
      {rows.map((r) => {
        const pct = total ? Math.max(2, (r.value / total) * 100) : 0;
        return (
          <div key={r.key} className="dash-funnel-row">
            <div className="dash-funnel-label">{r.label}</div>
            <div className="dash-funnel-bar">
              <div className="dash-funnel-fill" style={{ width: `${pct}%`, background: r.color }} />
            </div>
            <div className="dash-funnel-value">
              {fmtNum(r.value)}
              <small>{total ? `${Math.round((r.value / total) * 100)}%` : ''}</small>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const StockAlert = ({ tone, icon, body }) => (
  <div className={`dash-alert tone-${tone}`}>
    <span className="dash-alert-icon"><i className={icon} /></span>
    <span className="dash-alert-body">{body}</span>
  </div>
);

const Tabs = ({ tabs, active, onChange }) => (
  <div className="dash-tabs" role="tablist">
    {tabs.map((tb) => (
      <button
        key={tb.key}
        type="button"
        role="tab"
        aria-selected={active === tb.key}
        className={`dash-tab ${active === tb.key ? 'active' : ''}`}
        onClick={() => onChange(tb.key)}
      >
        {tb.icon && <i className={tb.icon} />}
        <span>{tb.label}</span>
      </button>
    ))}
  </div>
);

// ---------- main ----------

const DashboardContent = (props) => {
  const { t } = props;
  const dispatch = useDispatch();

  const [tab, setTab] = useState('overview');

  // rangos por tab
  const [daysOverview, setDaysOverview] = useState(7);
  const [daysPatients, setDaysPatients] = useState(7);
  const [daysLab, setDaysLab]           = useState(7);
  const [practice, setPractice]         = useState('');

  // last updated por tab
  const [updatedAt, setUpdatedAt] = useState({ overview: null, kits: null, patients: null, lab: null });
  const tickUpdate = (key) => setUpdatedAt((p) => ({ ...p, [key]: new Date() }));

  const [labStats, setLabStats]     = useState(null);
  const [labHistory, setLabHistory] = useState(null);
  // counts de la practica seleccionada (override de KPIs Overview).
  const [practiceStats, setPracticeStats] = useState(null);

  // ---------- selectors ----------
  const dataDoctors   = useSelector((s) => s.systemReducer.systemDoctors);
  const dataPatients  = useSelector((s) => s.systemReducer.systemPatients);
  const dataPractices = useSelector((s) => s.practicesReducer.totalPractices);
  const dataConsent   = useSelector((s) => s.systemReducer.systemHL7Consent);
  const dataBilling   = useSelector((s) => s.billingReducer.studies);
  const kitsCount     = useSelector((s) => s.kitsReducer.kitsCount);
  const kitsStateCnt  = useSelector((s) => s.kitsReducer.kitsCountByState);
  const kitTypesMap   = useSelector((s) => s.kitsReducer.types);
  const allPractices  = useSelector((s) => s.practicesReducer.practices);

  // ---------- per-tab fetchers ----------
  // Helper: nombre de la práctica seleccionada (para billing que usa name, no id)
  const getPracticeName = useCallback(() => {
    const list = (allPractices && allPractices.practices) || [];
    const p = list.find((x) => String(x._id) === String(practice));
    return p ? p.name : null;
  }, [allPractices, practice]);

  const fetchOverview = useCallback(async (d) => {
    dispatch(actionGetAllDoctorsSystem(d));
    dispatch(actionGetAllPatientsSystem(d));
    dispatch(actionGetAllPractices(d));
    dispatch(actionGetBilling(d, getPracticeName()));
    if (practice) {
      try {
        const r = await PracticesService.getPracticeStats(practice);
        setPracticeStats(r && r.data ? r.data : null);
      } catch { setPracticeStats(null); }
    } else {
      setPracticeStats(null);
    }
    tickUpdate('overview');
  }, [dispatch, practice, getPracticeName]);

  const fetchKits = useCallback(() => {
    dispatch(actionGetKitsCount(practice || null));
    dispatch(actionGetKitsStateCount(practice || null));
    if (!Object.keys(kitTypesMap || {}).length) dispatch(actionGetKitTypes());
    tickUpdate('kits');
  }, [dispatch, kitTypesMap, practice]);

  const fetchPatients = useCallback((d) => {
    dispatch(actionGetAllPatientsSystem(d));
    dispatch(actionGetAllHl7Consent());
    tickUpdate('patients');
  }, [dispatch]);

  const fetchLab = useCallback(async (d) => {
    try {
      const cur = await LabStatsService.getCurrentStats();
      setLabStats(cur && cur.data ? cur.data : null);
    } catch { setLabStats(null); }
    try {
      const hist = await LabStatsService.getStatsHistory(`${d}d`, d <= 7 ? '1h' : '24h');
      setLabHistory(hist && hist.data ? hist.data : null);
    } catch { setLabHistory(null); }
    tickUpdate('lab');
  }, []);

  // initial
  useEffect(() => {
    fetchOverview(daysOverview);
    fetchKits();
    fetchPatients(daysPatients);
    fetchLab(daysLab);
    // Lista de prácticas (para el filtro de la toolbar)
    dispatch(actionGetPractices(0, 999));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // re-fetch only when its own days changes
  useEffect(() => { fetchOverview(daysOverview); /* eslint-disable-next-line */ }, [daysOverview]);
  useEffect(() => { fetchPatients(daysPatients); /* eslint-disable-next-line */ }, [daysPatients]);
  useEffect(() => { fetchLab(daysLab);           /* eslint-disable-next-line */ }, [daysLab]);

  // refresca al cambiar la practica.
  useEffect(() => {
    if (allPractices) { // billing necesita el name, espera la lista cargada.
      fetchOverview(daysOverview);
      fetchKits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practice]);

  // ---------- derived ----------
  const totalKits = kitsStateCnt && kitsStateCnt.all ? kitsStateCnt.all : 0;

  const kitStatusItems = useMemo(() => {
    if (!kitsStateCnt) return [];
    return Object.keys(kitsStateCnt)
      .filter((k) => k !== 'all' && kitsStateCnt[k] > 0)
      .map((k) => ({ key: k, label: t(k) || k, value: kitsStateCnt[k], color: KIT_STATUS_COLOR[k] || C.muted }))
      .sort((a, b) => b.value - a.value);
  }, [kitsStateCnt, t]);

  const lifecycleFunnel = useMemo(() => {
    if (!kitsStateCnt) return [];
    const sum = (...keys) => keys.reduce((a, k) => a + (kitsStateCnt[k] || 0), 0);
    return [
      { key: 'setup',    label: t('lc-setup')    || 'Setup',    value: sum('k_st_1', 'k_st_disassembled', 'k_st_2'), color: C.success },
      { key: 'practice', label: t('lc-practice') || 'Practice', value: sum('k_st_reserved', 'k_st_3'),         color: C.info    },
      { key: 'patient',  label: t('lc-patient')  || 'Patient',  value: sum('k_st_4', 'k_st_5', 'k_st_9'), color: C.primary },
      { key: 'done',     label: t('lc-done')     || 'Done',     value: sum('k_st_6', 'k_st_11', 'k_st_8', 'k_st_10', 'k_st_7'), color: C.dark },
    ];
  }, [kitsStateCnt, t]);

  const kitsByTypeChart = useMemo(() => {
    if (!kitsCount || !Object.keys(kitsCount).length) return null;
    const labels = Object.keys(kitsCount);
    return {
      labels,
      datasets: [{
        label: t('kits-available') || 'Available',
        data: labels.map((l) => kitsCount[l] || 0),
        backgroundColor: labels.map(() => C.info),
        borderRadius: 4,
        maxBarThickness: 40,
      }],
    };
  }, [kitsCount, t]);

  const alerts = useMemo(() => {
    const out = [];
    if (kitsStateCnt) {
      const ready = kitsStateCnt.k_st_2 || 0;
      if (ready < 10) {
        out.push({ tone: 'warning', icon: 'now-ui-icons ui-1_bell-53',
          body: <><strong>{ready}</strong> {t('kits-ready-low') || 'kits ready — stock under minimum'}</> });
      }
      const stuck = kitsStateCnt['k_st_5'] || 0;
      if (stuck > 50) {
        out.push({ tone: 'danger', icon: 'now-ui-icons ui-2_time-alarm',
          body: <><strong>{stuck}</strong> {t('kits-in-production-many') || 'kits in production — review fulfillment'}</> });
      }
      const returned = kitsStateCnt.k_st_6 || 0;
      if (returned > 0) {
        out.push({ tone: 'info', icon: 'now-ui-icons arrows-1_refresh-69',
          body: <><strong>{returned}</strong> {t('kits-returned') || 'kits returned — needs disassembly'}</> });
      }
    }
    if (out.length === 0) {
      out.push({ tone: 'success', icon: 'now-ui-icons ui-1_check',
        body: t('kits-no-alerts') || 'No stock alerts at this time.' });
    }
    return out;
  }, [kitsStateCnt, t]);

  const patientSourcesChart = useMemo(() => {
    if (!dataPatients) return null;
    const sources = [
      { label: 'BOS',                     value: dataPatients.from_BOS  || 0 },
      { label: 'PIFS',                    value: dataPatients.from_PIFS || 0 },
      { label: t('register') || 'Register form', value: dataPatients.from_form || 0 },
    ];
    return {
      labels: sources.map((s) => s.label),
      datasets: [{
        label: t('patients') || 'Patients',
        data: sources.map((s) => s.value),
        backgroundColor: [C.info, C.primary, C.warning],
        borderRadius: 4,
        maxBarThickness: 60,
      }],
    };
  }, [dataPatients, t]);

  const consentTotal = (dataConsent && (dataConsent.approved || 0) + (dataConsent.denied || 0)) || 0;
  const consentItems = [
    { key: 'approved', label: t('consent-approved') || 'Approved', value: dataConsent && dataConsent.approved || 0, color: C.success },
    { key: 'denied',   label: t('consent-denied')   || 'Denied',   value: dataConsent && dataConsent.denied   || 0, color: C.danger  },
  ];

  // KPIs Overview — cuando hay práctica seleccionada, override doctors/patients/centers
  // con los datos compactos de PracticesService.getPracticeStats (sin delta).
  const filtered = !!practice;
  const kpis = filtered ? [
    {
      label: t('kpi-doctors') || 'Healthcare professionals',
      value: practiceStats ? practiceStats.doctors : null,
      delta: null,
      deltaLabel: t('in-this-practice') || 'in this practice',
      icon: 'now-ui-icons users_circle-08',
      tone: 'info',
    },
    {
      label: t('kpi-patients') || 'Patients',
      value: practiceStats ? practiceStats.patients : null,
      delta: null,
      deltaLabel: t('in-this-practice') || 'in this practice',
      icon: 'now-ui-icons users_single-02',
      tone: 'primary',
    },
    {
      label: t('kpi-practices') || 'Medical centers',
      value: 1,
      delta: null,
      deltaLabel: t('selected') || 'selected',
      icon: 'now-ui-icons business_briefcase-24',
      tone: 'success',
    },
    {
      label: t('kpi-studies') || 'Studies processed',
      value: dataBilling && dataBilling.studies,
      delta: null,
      deltaLabel: `${t('last') || 'last'} ${daysOverview}d`,
      icon: 'now-ui-icons design_app',
      tone: 'warning',
    },
  ] : [
    {
      label: t('kpi-doctors') || 'Healthcare professionals',
      value: dataDoctors && dataDoctors.users,
      delta: calcDelta(dataDoctors && dataDoctors.newUsers, (dataDoctors && dataDoctors.users || 0) - (dataDoctors && dataDoctors.newUsers || 0)),
      deltaLabel: `${t('vs-prev')} ${daysOverview}d`,
      icon: 'now-ui-icons users_circle-08',
      tone: 'info',
    },
    {
      label: t('kpi-patients') || 'Patients',
      value: dataPatients && dataPatients.users,
      delta: calcDelta(dataPatients && dataPatients.newUsers, (dataPatients && dataPatients.users || 0) - (dataPatients && dataPatients.newUsers || 0)),
      deltaLabel: `${t('vs-prev')} ${daysOverview}d`,
      icon: 'now-ui-icons users_single-02',
      tone: 'primary',
    },
    {
      label: t('kpi-practices') || 'Medical centers',
      value: dataPractices && dataPractices.practices,
      delta: calcDelta(dataPractices && dataPractices.newPractices, (dataPractices && dataPractices.practices || 0) - (dataPractices && dataPractices.newPractices || 0)),
      deltaLabel: `${t('vs-prev')} ${daysOverview}d`,
      icon: 'now-ui-icons business_briefcase-24',
      tone: 'success',
    },
    {
      label: t('kpi-studies') || 'Studies processed',
      value: dataBilling && dataBilling.studies,
      delta: null,
      deltaLabel: `${t('last') || 'last'} ${daysOverview}d`,
      icon: 'now-ui-icons design_app',
      tone: 'warning',
    },
  ];

  const tabs = [
    { key: 'overview',  label: t('tab-overview')  || 'Overview',           icon: 'now-ui-icons design_bullet-list-67' },
    { key: 'kits',      label: t('tab-kits')      || 'Kits & Inventory',   icon: 'now-ui-icons shopping_box' },
    { key: 'patients',  label: t('tab-patients')  || 'Patients & Consent', icon: 'now-ui-icons users_single-02' },
    { key: 'lab',       label: t('tab-lab')       || 'Lab activity',       icon: 'now-ui-icons design_app' },
  ];

  return (
    <div className="content dashboard-page mt-3">

      {/* Toolbar global (sin range — cada tab tiene el suyo).
          El tab Lab consume stats de MS-API-Lab que NO estan tagueadas por
          practica, asi que el filtro se deshabilita ahi para no dar la falsa
          impresion de que se puede acotar. */}
      <div className="dash-toolbar">
        <h1 className="dash-toolbar-title">{t('title') || 'Dashboard'}</h1>
        <div className="dash-toolbar-spacer" />
        <Input
          type="select"
          bsSize="sm"
          value={tab === 'lab' ? '' : practice}
          disabled={tab === 'lab'}
          onChange={(e) => setPractice(e.target.value)}
          className="dash-practice-select"
          title={tab === 'lab'
            ? (t('lab-no-practice-filter') || 'Lab activity stats are global — practice filter does not apply')
            : undefined}
        >
          <option value="">
            {tab === 'lab'
              ? (t('lab-all-global') || 'Global (lab no soporta filtro por practica)')
              : (t('all-practices') || 'All practices')}
          </option>
          {/* state.practicesReducer.practices viene como {practices: [...], pagination} desde el endpoint */}
          {(allPractices && Array.isArray(allPractices.practices) ? allPractices.practices : []).map((p) => (
            <option key={p._id} value={p._id}>{p.name}</option>
          ))}
        </Input>
      </div>

      <div className="dash-main-card">
        <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {/* ===== Overview tab ===== */}
      {tab === 'overview' && (
        <div className="dash-tab-card">
          <RangeSelector
            days={daysOverview}
            onChange={setDaysOverview}
            onRefresh={() => fetchOverview(daysOverview)}
            lastUpdated={updatedAt.overview}
            t={t}
          />
          <div className="dash-kpi-grid">
            {kpis.map((k, i) => <MetricCard key={i} {...k} />)}
          </div>
        </div>
      )}

      {/* ===== Kits tab ===== */}
      {tab === 'kits' && (
        <div className="dash-tab-card">
          <RangeSelector
            days={null}
            onChange={() => {}}
            onRefresh={fetchKits}
            lastUpdated={updatedAt.kits}
            t={t}
            hideLabel
          />
          <Section
            icon="now-ui-icons shopping_box"
            title={t('section-kits') || 'Kits & Inventory'}
            hint={`${fmtNum(totalKits)} ${t('total') || 'total'}`}
          >
            <div className="dash-grid-2">
              <div>
                <h6 className="dash-subhead">{t('kits-status-distribution') || 'Status distribution'}</h6>
                <DonutWithLegend items={kitStatusItems} total={totalKits} t={t} />
              </div>
              <div>
                <h6 className="dash-subhead">{t('kits-lifecycle-funnel') || 'Lifecycle distribution'}</h6>
                <Funnel rows={lifecycleFunnel} total={totalKits} />
              </div>
            </div>

            <hr className="dash-section-divider" />

            <div className="dash-grid-2">
              <div>
                <h6 className="dash-subhead">{t('kits-by-type') || 'Kits by type (available)'}</h6>
                {kitsByTypeChart ? (
                  <div className="dash-chart-wrap">
                    <Bar
                      data={kitsByTypeChart}
                      options={{
                        maintainAspectRatio: false,
                        legend: { display: false },
                        scales: {
                          yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }],
                          xAxes: [{ gridLines: { display: false } }],
                        },
                      }}
                    />
                  </div>
                ) : <div className="dash-empty">{t('no-data') || 'No data'}</div>}
              </div>
              <div>
                <h6 className="dash-subhead">{t('stock-alerts') || 'Stock & operational alerts'}</h6>
                <div className="dash-alert-row">
                  {alerts.map((a, i) => <StockAlert key={i} {...a} />)}
                </div>
              </div>
            </div>
          </Section>
        </div>
      )}

      {/* ===== Patients tab ===== */}
      {tab === 'patients' && (
        <div className="dash-tab-card">
          <RangeSelector
            days={daysPatients}
            onChange={setDaysPatients}
            onRefresh={() => fetchPatients(daysPatients)}
            lastUpdated={updatedAt.patients}
            t={t}
          />
          {filtered && (
            <div className="dash-filter-disclaimer">
              <i className="now-ui-icons ui-1_bell-53" />
              <span>{t('not-filtered-by-practice') || 'These metrics are system-wide; practice filter does not apply yet.'}</span>
            </div>
          )}
          <div className="dash-grid-2">
            <Section icon="now-ui-icons users_single-02" title={t('section-patient-sources') || 'Patient acquisition sources'} hint={t('section-patient-sources-hint') || 'breakdown by channel'}>
              {patientSourcesChart ? (
                <div className="dash-chart-wrap">
                  <Bar
                    data={patientSourcesChart}
                    options={{
                      maintainAspectRatio: false,
                      legend: { display: false },
                      scales: {
                        yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }],
                        xAxes: [{ gridLines: { display: false } }],
                      },
                    }}
                  />
                </div>
              ) : <div className="dash-empty">{t('no-data') || 'No data'}</div>}
            </Section>
            <Section icon="now-ui-icons ui-1_check" title={t('section-consent') || 'HL7 Consent'} hint={`${fmtNum(consentTotal)} ${t('total') || 'total'}`}>
              <DonutWithLegend items={consentItems} total={consentTotal} t={t} />
            </Section>
          </div>
        </div>
      )}

      {/* ===== Lab tab ===== */}
      {tab === 'lab' && (
        <div className="dash-tab-card">
          <RangeSelector
            days={daysLab}
            onChange={setDaysLab}
            onRefresh={() => fetchLab(daysLab)}
            lastUpdated={updatedAt.lab}
            t={t}
          />
          {filtered && (
            <div className="dash-filter-disclaimer">
              <i className="now-ui-icons ui-1_bell-53" />
              <span>{t('not-filtered-by-practice') || 'These metrics are system-wide; practice filter does not apply yet.'}</span>
            </div>
          )}
          <Section icon="now-ui-icons design_app" title={t('section-lab-activity') || 'Lab activity'} hint={t('section-lab-activity-hint') || 'Source: MS-API-Lab'}>
            {!labStats ? (
              <div className="dash-empty">{t('lab-unavailable') || 'Lab service unavailable'}</div>
            ) : (
              <>
                <div className="dash-kpi-grid" style={{ marginBottom: '1rem' }}>
                  <MetricCard label={t('lab-total-requests') || 'Total requests'}    value={labStats.totalRequests}        icon="now-ui-icons arrows-1_share-66" tone="info"    />
                  <MetricCard label={t('lab-files-uploaded') || 'Files uploaded'}    value={labStats.filesUploaded}        icon="now-ui-icons files_paper"       tone="primary" />
                  <MetricCard label={t('lab-total-size')     || 'Total size'}        value={formatBytes(labStats.totalFileSize)} icon="now-ui-icons design_image" tone="success" />
                  <MetricCard label={t('lab-endpoints-active') || 'Active endpoints'} value={labStats.endpointStats ? Object.keys(labStats.endpointStats).length : 0} icon="now-ui-icons ui-2_settings-90" tone="warning" />
                </div>
                {labHistory && labHistory.timeSeriesData && labHistory.timeSeriesData.length > 0 ? (
                  <div className="dash-chart-wrap">
                    <Line
                      data={buildLineFromHistory(labHistory.timeSeriesData)}
                      options={{
                        maintainAspectRatio: false,
                        legend: { display: true, position: 'bottom' },
                        scales: {
                          yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }],
                          xAxes: [{ gridLines: { display: false } }],
                        },
                        tooltips: { mode: 'index', intersect: false },
                      }}
                    />
                  </div>
                ) : (
                  <div className="dash-empty">{t('lab-no-history') || 'No historical activity in the selected range.'}</div>
                )}
              </>
            )}
          </Section>
        </div>
      )}

      </div>
    </div>
  );
};

export default translate('dashboard')(DashboardContent);
